import React, { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import { FiltersService } from '../../services/FiltersService';
import { DROPDOWN_CODES } from '../../constants/dropdown.constants';
import SimpleDropdown from './SimpleDropdown';

interface SpecializationDropdownProps {
  value?: string;
  onChange?: (value: string) => void;
  courseCode?: string; // Parent course code to load specializations
  placeholder?: string;
  disabled?: boolean;
  autoLoad?: boolean; // Whether to automatically load specializations when courseCode changes
  onDropdownLoad?: () => void; // Callback when dropdown options are loaded
}

const SpecializationDropdown: React.FC<SpecializationDropdownProps> = ({
  value,
  onChange = () => {},
  courseCode,
  placeholder = "Select Specialization",
  disabled = false,
  autoLoad = true,
  onDropdownLoad
}) => {
  const [specializationOptions, setSpecializationOptions] = useState<{ key: string, label: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const onDropdownLoadRef = useRef(onDropdownLoad);

  useEffect(() => {
    onDropdownLoadRef.current = onDropdownLoad;
  }, [onDropdownLoad]);

  const loadSpecializationsForCourse = useCallback(async (parentCourseCode: string) => {
    if (!parentCourseCode) {
      setSpecializationOptions([]);
      onDropdownLoadRef.current?.();
      return;
    }

    setLoading(true);
    try {
      const specializations = await FiltersService.getDropdownItems(DROPDOWN_CODES.Specialization, parentCourseCode);
      setSpecializationOptions([...specializations.map(item => ({
        key: (item.value || '').trim().toUpperCase(),
        label: item.label || ''
      }))]);
      onDropdownLoadRef.current?.();
    } catch (error) {
      console.error('Failed to load specializations for course:', parentCourseCode, error);
      message.error('Failed to load specialization options');
      setSpecializationOptions([]);
      onDropdownLoadRef.current?.();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoLoad && courseCode) {
      loadSpecializationsForCourse(courseCode);
    } else if (!courseCode) {
      setSpecializationOptions([]);
      onDropdownLoadRef.current?.();
    }
  }, [autoLoad, courseCode, loadSpecializationsForCourse]);

  const handleSpecializationChange = (specializationCode: string) => {
    onChange(specializationCode);
  };

  const getPlaceholder = () => {
    if (loading) return "Loading...";
    if (!courseCode) return "Please select a course first";
    return placeholder;
  };

  const isDisabled = disabled || loading || !courseCode;

  // DEBUG LOG for dropdown value debugging
  console.log('SpecializationDropdown value:', value, 'options:', specializationOptions);

  return (
    <SimpleDropdown
      options={specializationOptions}
      value={value}
      onChange={handleSpecializationChange}
      placeholder={getPlaceholder()}
      disabled={isDisabled}
    />
  );
};

export default SpecializationDropdown; 